import { Checkbox } from "@mui/material";
import Box from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Typography } from "@mui/material";
import Iconify from "components/iconify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Formik, useFormikContext } from "formik";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

import BannerInput from "components/bannerInput";
import Button from "components/button";
import CalendarField, { monthsLong } from "components/calendarField";
import FileInput from "components/fileInput";
import PhoneNumber from "components/phoneInput";
import SelectOptions from "components/selectOptions";
import { Select } from "components/hook-form";

import { ReactComponent as ArrowDown } from "assets/images/arrowDown.svg";
import { ReactComponent as Cabinbaggage } from "assets/images/cabinbaggage.svg";
import { duration } from "utils/Utils";

dayjs.extend(customParseFormat);

import { SavedTravelersModal } from "components/app-modals";
import { countries } from "assets/data";
import { getUserFromLocalStorage } from "hooks/LocalStorage";
import { useTranslation } from "i18n";
import { getCountryCode } from "utils/Currency&Language";
import {
  applySavedTravelerToSlot,
  savedTravelerId,
  savedTravelerName,
} from "utils/savedTravelers";

function PassengersDetails({
  state,
  travelersInit,
  validationSchema,
  handelInfoSubmit,
  setCode,
  source,
  expiryAcknowledgedRef,
}) {
  const { locale, t } = useTranslation();
  const [openSegments, setOpenSegments] = useState({});
  const [errImg, setErrImg] = useState(false);
  const [validateChecked1, setValidateChecked1] = useState(false);
  const [validateChecked2, setValidateChecked2] = useState(false);
  const [validateChecked3, setValidateChecked3] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [expiryWarnings, setExpiryWarnings] = useState([]);
  const pendingSubmitRef = useRef(null);
  const [savedPickerIndex, setSavedPickerIndex] = useState(null);
  const [appliedSaved, setAppliedSaved] = useState({});
  const userType = getUserFromLocalStorage()?.type;
  const canUseSavedTravelers = ["MEMBER", "B_AGENT", "ADMIN", "EMPLOYEE"].includes(
    userType,
  );

  const FocusError = () => {
    const { errors, isSubmitting, isValidating } = useFormikContext();

    useEffect(() => {
      if (isSubmitting && !isValidating) {
        let keys = Object.keys(errors);

        if (keys.length > 0) {
          const selector = `[name=${keys[0]}]`;
          const errorElement = document.querySelector(selector);

          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            errorElement.focus({ preventScroll: true });
          }
        }
      }
    }, [errors, isSubmitting, isValidating]);

    return null;
  };

  const gender = [
    { value: "Male", label: t("booking.options.male") },
    { value: "Female", label: t("booking.options.female") },
  ];
  const getTravelerTypeLabel = (type) => t(`booking.travelerTypes.${type}`);
  const travelerCount = state?.flight?.travelerPricings?.length || 0;
  const consentCount = [validateChecked1, validateChecked2, validateChecked3].filter(
    Boolean,
  ).length;

  const getLocation = (iata) => state?.dictionaries?.locations?.[iata];
  const getCabin = (segmentId) =>
    state?.flight?.travelerPricings?.[0]?.fareDetailsBySegment?.find(
      (sege) => sege?.segmentId == segmentId,
    )?.cabin;
  const getAirlineLogo = (carrierCode) => {
    const logos = state?.dictionaries?.airlineLogos?.[carrierCode];
    return logos?.[0]?.tailImage || logos?.[0]?.squareImage;
  };

  const toggleSegment = (key) => {
    setOpenSegments((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const CountrySelect = ({ label, name }) => (
    <Select
      label={label}
      mandatory
      options={countries.map((country) => country.label)}
      getOptionLabel={(option) => option}
      name={name}
      type="country"
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option) => {
        const { code, label: countryLabel } = countries.filter(
          (country) => country.label === option,
        )[0];

        if (!countryLabel) {
          return null;
        }

        return (
          <li {...props} key={countryLabel}>
            <Iconify
              key={countryLabel}
              icon={`circle-flags:${code.toLowerCase()}`}
              width={22}
              sx={{ mr: 1, maxWidth: 22, minWidth: 22 }}
            />
            {countryLabel} ({code})
          </li>
        );
      }}
    />
  );

  return (
    <div className="bk-s1">
      <section className="bk-s1-trip">
        <div className="bk-s1-trip-head">
          <div>
            <p className="bk-s1-kicker">{t("booking.labels.itinerary")}</p>
            <h2>{t("booking.labels.tripSummary")}</h2>
          </div>
          <span className="bk-s1-trip-count">
            {state?.flight?.itineraries?.length || 0}{" "}
            {(state?.flight?.itineraries?.length || 0) === 1
              ? t("booking.labels.flight")
              : t("booking.labels.flights")}
          </span>
        </div>

        <div className="bk-s1-itin-list">
          {state?.flight?.itineraries?.map((item, itineraryIndex) => {
            const stopCount = Math.max((item?.segments?.length || 1) - 1, 0);

            return (
              <article className="bk-s1-itin" key={itineraryIndex}>
                <div className="bk-s1-itin-top">
                  <span className="bk-s1-itin-badge">
                    {state?.flightDetails?.return === "Multi-city" ? (
                      t("booking.labels.trip", { number: itineraryIndex + 1 })
                    ) : itineraryIndex === 0 ? (
                      t("booking.labels.departure")
                    ) : (
                      t("booking.labels.return")
                    )}
                  </span>
                  <span className="bk-s1-itin-meta">
                    {stopCount === 0
                      ? t("booking.labels.direct")
                      : t("booking.labels.stopCount", { count: stopCount })}
                    <span aria-hidden="true">·</span>
                    {t("booking.labels.duration")} {duration(item?.duration)}
                  </span>
                </div>

                {item?.segments?.map((segment, segmentIndex) => {
                  const key = `${itineraryIndex}-${segmentIndex}`;
                  const isOpen = Boolean(openSegments[key]);
                  const from = getLocation(segment?.departure?.iataCode);
                  const to = getLocation(segment?.arrival?.iataCode);
                  const logo = getAirlineLogo(segment?.carrierCode);
                  const cabin = getCabin(segment?.id);

                  return (
                    <div className="bk-s1-seg" key={key}>
                      {item.segments.length > 1 && (
                        <p className="bk-s1-seg-cities">
                          {from?.city || segment?.departure?.iataCode}
                          <span aria-hidden="true">→</span>
                          {to?.city || segment?.arrival?.iataCode}
                        </p>
                      )}

                      <div className="bk-s1-seg-route">
                        <div className="bk-s1-seg-point">
                          <strong>
                            {moment(segment?.departure?.at).format("H:mm")}
                          </strong>
                          <span>{segment?.departure?.iataCode}</span>
                          <small>
                            {moment(segment?.departure?.at)
                              .locale(locale)
                              .format("ddd D MMM")}
                          </small>
                        </div>

                        <div className="bk-s1-seg-line">
                          <span>{duration(segment?.duration, item?.duration)}</span>
                        </div>

                        <div className="bk-s1-seg-point is-end">
                          <strong>
                            {moment(segment?.arrival?.at).format("H:mm")}
                          </strong>
                          <span>{segment?.arrival?.iataCode}</span>
                          <small>
                            {moment(segment?.arrival?.at)
                              .locale(locale)
                              .format("ddd D MMM")}
                          </small>
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`bk-s1-seg-toggle${isOpen ? " is-open" : ""}`}
                        onClick={() => toggleSegment(key)}
                      >
                        <span className="bk-s1-seg-carrier">
                          {logo ? (
                            <img
                              className="bk-s1-airline"
                              src={logo}
                              alt=""
                            />
                          ) : null}
                          {cabin && <em>{cabin}</em>}
                          <span>
                            {t("booking.labels.flightNo")} {segment?.number}
                          </span>
                        </span>
                        <span className="bk-s1-seg-more">
                          {isOpen
                            ? t("booking.labels.hideFlightDetails")
                            : t("booking.labels.showFlightDetails")}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="bk-s1-seg-details">
                          <p>
                            <span>{t("booking.labels.airline")}</span>
                            {state?.dictionaries?.carriers?.[segment?.carrierCode] ||
                              segment?.carrierCode}
                          </p>
                          <p>
                            <span>{t("booking.labels.aircraft")}</span>
                            {segment?.aircraft?.code || "—"}
                          </p>
                          <p>
                            <span>{from?.airport || segment?.departure?.iataCode}</span>
                            <span aria-hidden="true">→</span>
                            {to?.airport || segment?.arrival?.iataCode}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </article>
            );
          })}
        </div>
      </section>

      <Formik
        initialValues={travelersInit}
        validationSchema={validationSchema}
        onSubmit={handelInfoSubmit}
        enableReinitialize={true}
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          setFieldTouched,
          setFieldValue,
          setFieldError,
          touched,
          values,
        }) => (
          <>
            <div className="form-inputs">
              <div className="bk-s1-people-head">
                <div>
                  <p className="bk-s1-kicker">{t("booking.labels.passengers")}</p>
                  <h2>{t("booking.labels.travelersInfo")}</h2>
                  <p className="bk-s1-people-copy">
                    {t("booking.messages.travelersHint")}
                  </p>
                </div>
                {travelerCount > 1 && (
                  <nav className="bk-s1-jump" aria-label={t("booking.labels.passengers")}>
                    {state?.flight?.travelerPricings?.map((item, index) => (
                      <a
                        key={index}
                        href={`#traveler-${index}`}
                        className="bk-s1-jump-item"
                      >
                        {index + 1}
                        <span>{getTravelerTypeLabel(item.travelerType)}</span>
                      </a>
                    ))}
                  </nav>
                )}
              </div>

              <FocusError />

              {state?.flight?.travelerPricings?.map((item, index) => {
                const isAdultContact =
                  item.travelerType !== "CHILD" &&
                  item.travelerType !== "HELD_INFANT";
                const bags = item?.fareDetailsBySegment?.[0]?.includedCheckedBags;
                const hasBags = Boolean(
                  (bags?.quantity && bags.quantity !== 0) || bags?.weight,
                );

                return (
                  <section
                    className="bk-s1-person"
                    id={`traveler-${index}`}
                    key={index}
                  >
                    <header className="bk-s1-person-head">
                      <span className="bk-s1-person-num">{index + 1}</span>
                      <div>
                        <p className="bk-s1-person-title">
                          {t("booking.labels.travelerCard", { number: index + 1 })}
                        </p>
                        <p className="bk-s1-person-sub">
                          {t("booking.labels.primaryPassenger", {
                            type: getTravelerTypeLabel(item.travelerType),
                          })}
                        </p>
                      </div>
                      {appliedSaved[index] ? (
                        <span className="bk-s1-person-saved">
                          {t("booking.existingTravelers.applied")}
                        </span>
                      ) : (
                        <span className="bk-s1-person-type">
                          {getTravelerTypeLabel(item.travelerType)}
                        </span>
                      )}
                    </header>

                    {errImg && <p className="bk-form-error">{errImg}</p>}

                    {canUseSavedTravelers && (
                      <button
                        type="button"
                        className="bk-s1-saved"
                        onClick={() => setSavedPickerIndex(index)}
                      >
                        <span className="bk-s1-saved-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                          </svg>
                        </span>
                        <div>
                          <p>
                            {appliedSaved[index]?.name ||
                              t("booking.existingTravelers.choose")}
                          </p>
                          <span>
                            {appliedSaved[index]
                              ? t("booking.existingTravelers.change")
                              : t("booking.existingTravelers.chooseHint")}
                          </span>
                        </div>
                        <em>
                          {appliedSaved[index]
                            ? t("booking.existingTravelers.change")
                            : t("booking.existingTravelers.use")}
                        </em>
                      </button>
                    )}

                    <div className="bk-s1-scan">
                      <div className="bk-s1-scan-copy">
                        <span className="bk-s1-scan-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24">
                            <path d="M12 15.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                          </svg>
                        </span>
                        <div>
                          <p>{t("booking.labels.scanPassport")}</p>
                          <span>{t("booking.messages.uploadPassport")}</span>
                        </div>
                      </div>
                      <div className="bk-upload-actions">
                        <FileInput
                          index={index}
                          setErrImg={setErrImg}
                          departureDate={
                            state?.flight?.itineraries[0]?.segments[0]?.departure
                              ?.at
                          }
                          hasProgress
                          onFilled={() =>
                            setAppliedSaved((prev) => {
                              const next = { ...prev };
                              delete next[index];
                              return next;
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="bk-s1-block">
                      <p className="bk-s1-block-title">
                        {t("booking.labels.personalDetails")}
                      </p>
                      <Box
                        className="bk-field-row"
                        display="flex"
                        flexWrap={{ xs: "wrap", md: "nowrap" }}
                      >
                        <BannerInput
                          mandatory={true}
                          placeholder="e.g. Harry James"
                          label={t("booking.labels.firstName")}
                          onChange={handleChange(`firstName${index}`)}
                          error={
                            touched[`firstName${index}`] &&
                            errors[`firstName${index}`]
                          }
                          onBlur={() => setFieldTouched(`firstName${index}`)}
                          name={`firstName${index}`}
                          value={values[`firstName${index}`]}
                        />
                        <BannerInput
                          mandatory={true}
                          placeholder="e.g. Brown"
                          label={t("booking.labels.lastName")}
                          onChange={handleChange(`lastName${index}`)}
                          error={
                            touched[`lastName${index}`] &&
                            errors[`lastName${index}`]
                          }
                          name={`lastName${index}`}
                          onBlur={() => setFieldTouched(`lastName${index}`)}
                          value={values[`lastName${index}`]}
                        />
                      </Box>
                      <Box
                        className="bk-field-row"
                        display="flex"
                        flexWrap={{ xs: "wrap", md: "nowrap" }}
                        gap={2}
                      >
                        <CalendarField
                          valueDay={values[`dateOfBirthDay${index}`]}
                          valueMonth={values[`dateOfBirthMonth${index}`]}
                          valueYear={values[`dateOfBirthYear${index}`]}
                          error1={
                            touched[`dateOfBirthDay${index}`] &&
                            errors[`dateOfBirthDay${index}`]
                          }
                          error2={
                            errors[`dateOfBirthMonth${index}`] &&
                            touched[`dateOfBirthMonth${index}`]
                          }
                          error3={errors[`dateOfBirthYear${index}`]}
                          nameDay={`dateOfBirthDay${index}`}
                          nameMonth={`dateOfBirthMonth${index}`}
                          nameYear={`dateOfBirthYear${index}`}
                          onBlurDay={() =>
                            setFieldTouched(`dateOfBirthDay${index}`)
                          }
                          onBlurMonth={() =>
                            setFieldTouched(`dateOfBirthMonth${index}`)
                          }
                          onBlurYear={() =>
                            setFieldTouched(`dateOfBirthYear${index}`)
                          }
                          onChangeDay={handleChange(`dateOfBirthDay${index}`)}
                          onChangeMonth={handleChange(`dateOfBirthMonth${index}`)}
                          onChangeYear={handleChange(`dateOfBirthYear${index}`)}
                          mandatory={true}
                          label={t("booking.labels.dateOfBirth")}
                          index={index}
                          travelerType={item.travelerType}
                        />
                        <SelectOptions
                          mandatory
                          label={t("booking.labels.gender")}
                          options={gender}
                          name={`gender${index}`}
                          onChange={handleChange(`gender${index}`)}
                          error={
                            touched[`gender${index}`] && errors[`gender${index}`]
                          }
                          value={
                            gender.find(
                              (option) =>
                                option.value === values[`gender${index}`],
                            )?.label
                          }
                          onBlur={() => setFieldTouched(`gender${index}`)}
                        />
                      </Box>
                    </div>

                    {isAdultContact && (
                      <div className="bk-s1-block">
                        <p className="bk-s1-block-title">
                          {t("booking.labels.contactDetails")}
                        </p>
                        <Box
                          className="bk-field-row"
                          display="flex"
                          flexWrap={{ xs: "wrap", md: "nowrap" }}
                        >
                          <BannerInput
                            mandatory
                            placeholder="JohnDuo@gmail.com"
                            label={t("booking.labels.email")}
                            readOnly={source === "NDC"}
                            name={`emailAddress${index}`}
                            onChange={handleChange(`emailAddress${index}`)}
                            error={
                              touched[`emailAddress${index}`] &&
                              errors[`emailAddress${index}`]
                            }
                            onBlur={() =>
                              setFieldTouched(`emailAddress${index}`)
                            }
                            value={values[`emailAddress${index}`]}
                          />
                          <PhoneNumber
                            mandatory
                            label={t("booking.labels.phoneNumber")}
                            placeholder="009933466"
                            onChange={handleChange(`number${index}`)}
                            error={
                              touched[`number${index}`] &&
                              errors[`number${index}`]
                            }
                            setCode={setCode}
                            countryCode={[`countryCallingCode${index}`]}
                            name={`number${index}`}
                            onBlur={() => setFieldTouched(`number${index}`)}
                            value={values[`number${index}`]}
                          />
                        </Box>

                        {index !== 0 &&
                          state?.flight?.travelerPricings?.length > 1 && (
                            <Box mt={-1.2} mb={1}>
                              <FormControlLabel
                                disabled={
                                  !values[`emailAddress${0}`] ||
                                  !values[`number${0}`]
                                }
                                sx={{
                                  "& .MuiTypography-root": {
                                    fontSize: "13px",
                                  },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={checkedEmail}
                                    onChange={(event) => {
                                      setCheckedEmail(event.target.checked);

                                      if (event.target.checked) {
                                        state?.flight?.travelerPricings?.map(
                                          (traveler, ndx) => {
                                            if (ndx === 0) {
                                              return;
                                            } else {
                                              setFieldError(
                                                `emailAddress${ndx}`,
                                                null,
                                              );
                                              setFieldError(
                                                `number${ndx}`,
                                                null,
                                              );
                                              setFieldValue(
                                                `emailAddress${ndx}`,
                                                values[`emailAddress${0}`],
                                              );
                                              setFieldValue(
                                                `number${ndx}`,
                                                values[`number${0}`],
                                              );

                                              setFieldValue(
                                                `countryCallingCode${ndx}`,
                                                getCountryCode(
                                                  values[`number${0}`],
                                                )?.countryCode,
                                              );

                                              setFieldTouched(
                                                `number${ndx}`,
                                                false,
                                              );
                                              setFieldTouched(
                                                `emailAddress${ndx}`,
                                                false,
                                              );
                                            }
                                          },
                                        );
                                      } else {
                                        state?.flight?.travelerPricings?.map(
                                          (traveler, ndx) => {
                                            if (ndx === 0) {
                                              return;
                                            }

                                            setFieldValue(
                                              `emailAddress${ndx}`,
                                              "",
                                            );
                                            setFieldValue(`number${ndx}`, "");
                                          },
                                        );
                                      }
                                    }}
                                  />
                                }
                                label={t("booking.messages.sameEmailPhone")}
                              />
                            </Box>
                          )}
                      </div>
                    )}

                    <div className="bk-s1-block">
                      <p className="bk-s1-block-title">
                        {t("booking.labels.travelDocuments")}
                      </p>
                      <Box
                        className="bk-field-row"
                        display="flex"
                        flexWrap={{ xs: "wrap", md: "nowrap" }}
                      >
                        <BannerInput
                          label={t("booking.labels.passportNumber")}
                          onChange={
                            values[`documents${index}`]
                              ? handleChange(`documents${index}[0].number`)
                              : () => {}
                          }
                          error={
                            touched[`documents${index}[0].number`] &&
                            errors[`documents${index}[0].number`]
                          }
                          name={`documents${index}[0].number`}
                          onBlur={() =>
                            setFieldTouched(`documents${index}[0].number`)
                          }
                          value={values?.[`documents${index}`]?.[0]?.number}
                        />
                        <CalendarField
                          valueDay={values[`expDateDay${index}`]}
                          valueMonth={values[`expDateMonth${index}`]}
                          valueYear={values[`expDateYear${index}`]}
                          error1={
                            touched[`expDateDay${index}`] &&
                            errors[`expDateDay${index}`]
                          }
                          error2={
                            errors[`expDateMonth${index}`] &&
                            touched[`expDateMonth${index}`]
                          }
                          error3={
                            errors[`expDateYear${index}`] &&
                            touched[`expDateYear${index}`]
                          }
                          nameDay={`expDateDay${index}`}
                          nameMonth={`expDateMonth${index}`}
                          nameYear={`expDateYear${index}`}
                          onBlurDay={() =>
                            setFieldTouched(`expDateDay${index}`)
                          }
                          onBlurMonth={() =>
                            setFieldTouched(`expDateMonth${index}`)
                          }
                          onBlurYear={() =>
                            setFieldTouched(`expDateYear${index}`)
                          }
                          onChangeDay={handleChange(`expDateDay${index}`)}
                          onChangeMonth={handleChange(`expDateMonth${index}`)}
                          onChangeYear={handleChange(`expDateYear${index}`)}
                          mandatory={true}
                          label={t("booking.labels.passportExpirationDate")}
                          departureDate={
                            state?.flight?.itineraries[0]?.segments[0]?.departure
                              ?.at
                          }
                        />
                      </Box>

                      {values[`documents${index}`] && (
                        <Box
                          className="bk-field-row bk-field-row--docs"
                          display="flex"
                          flexWrap={{ xs: "wrap", md: "nowrap" }}
                        >
                          <CountrySelect
                            label={t("booking.labels.issuanceCountry")}
                            name={`documents${index}[0].issuanceCountry`}
                          />
                          <CountrySelect
                            label={t("booking.labels.nationality")}
                            name={`documents${index}[0].nationality`}
                          />
                        </Box>
                      )}
                    </div>

                    {item.travelerType === "ADULT" && (
                      <div className="bk-s1-block">
                        <p className="bk-s1-block-title">
                          {t("booking.labels.checkedBags")}
                        </p>
                        {!hasBags ? (
                          <div className="bk-s1-bags is-empty">
                            <p>{t("booking.messages.noBaggage")}</p>
                          </div>
                        ) : (
                          <div className="bk-s1-bags is-included">
                            <Cabinbaggage />
                            <div>
                              <p>{t("booking.labels.checkedBag")}</p>
                              <span>
                                {bags?.quantity ?? "1"} × {bags?.weight ?? "23"}{" "}
                                {bags?.weightUnit ?? "KG"}
                              </span>
                            </div>
                            <em>{t("booking.labels.included")}</em>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>

            <section className="bk-s1-consent">
              <div className="bk-s1-consent-head">
                <h2>{t("booking.labels.confirmations")}</h2>
                <span>
                  {t("booking.labels.consentProgress", { count: consentCount })}
                </span>
              </div>
              <label
                className={`bk-consent ${validateChecked1 ? "is-checked" : ""}`}
              >
                <Checkbox
                  checked={validateChecked1}
                  onClick={() => setValidateChecked1((prev) => !prev)}
                  disableRipple
                />
                <Typography>{t("booking.messages.verifyInfo")}</Typography>
              </label>
              <label
                className={`bk-consent ${validateChecked2 ? "is-checked" : ""}`}
              >
                <Checkbox
                  checked={validateChecked2}
                  onClick={() => setValidateChecked2((prev) => !prev)}
                  disableRipple
                />
                <Typography>{t("booking.messages.scannerNotice")}</Typography>
              </label>
              <label
                className={`bk-consent ${validateChecked3 ? "is-checked" : ""}`}
              >
                <Checkbox
                  checked={validateChecked3}
                  onClick={() => setValidateChecked3((prev) => !prev)}
                  disableRipple
                />
                <Typography>
                  {t("booking.messages.visaNotice", {
                    destination:
                      state?.flightDetails?.to?.split(",")?.length > 1
                        ? state?.flightDetails?.to?.split(",")[0]
                        : state?.flightDetails?.to,
                  })}{" "}
                  {state?.flightDetails?.returnDate && (
                    <>
                      {t("booking.messages.visaDuration", {
                        days:
                          moment(state?.flightDetails?.returnDate).diff(
                            moment(state?.flightDetails?.departureDate),
                            "days",
                          ) + 1,
                      })}
                    </>
                  )}{" "}
                </Typography>
              </label>
            </section>

            <div className="bk-s1-footer">
              <div className="bk-s1-footer-copy">
                <p>{t("booking.labels.readyToContinue")}</p>
                <span>
                  {t("booking.labels.travelerReady", { count: travelerCount })}
                </span>
              </div>
              <Button
                type="submit"
                onClick={() => {
                  const warnings = [];
                  state?.flight?.travelerPricings?.forEach((_, idx) => {
                    if (expiryAcknowledgedRef.current[idx]) return;

                    const rawDay = values[`expDateDay${idx}`];
                    const rawMonth = values[`expDateMonth${idx}`];
                    const year = values[`expDateYear${idx}`];
                    const deDate =
                      state?.flight?.itineraries?.[0]?.segments?.[0]?.departure
                        ?.at;

                    if (!rawDay || !rawMonth || !year || !deDate) return;

                    const day = String(rawDay).padStart(2, "0");
                    const month =
                      String(rawMonth).length > 2
                        ? monthsLong[rawMonth] || rawMonth
                        : String(rawMonth).padStart(2, "0");

                    const expirationDate = dayjs(
                      `${month}-${day}-${year}`,
                      "MM-DD-YYYY",
                      true,
                    );
                    const departureDate = dayjs(deDate);

                    if (!expirationDate.isValid()) {
                      warnings.push(
                        t("booking.messages.travelerExpirationUnverified", {
                          number: idx + 1,
                        }),
                      );
                    } else if (
                      !expirationDate.isAfter(departureDate.add(6, "months"))
                    ) {
                      warnings.push(
                        t("booking.messages.travelerExpirationWarning", {
                          number: idx + 1,
                          date: expirationDate
                            .locale(locale)
                            .format("MMM D, YYYY"),
                        }),
                      );
                    }
                  });

                  if (warnings.length > 0) {
                    setExpiryWarnings(warnings);
                    setShowExpiryModal(true);
                    pendingSubmitRef.current = handleSubmit;
                  } else {
                    handleSubmit();
                  }
                }}
                disabled={
                  !validateChecked1 ||
                  !validateChecked2 ||
                  !validateChecked3 ||
                  !(state?.flight?.travelerPricings || [])
                    .map((traveler, i) =>
                      traveler.travelerType !== "CHILD" &&
                      traveler.travelerType !== "HELD_INFANT"
                        ? i
                        : null,
                    )
                    .filter((i) => i !== null)
                    .every(
                      (idx) =>
                        values[`emailAddress${idx}`]?.trim?.() &&
                        values[`number${idx}`]?.trim?.(),
                    )
                }
                className="bigBtn primary-color booking"
              >
                {t("booking.actions.continue")}
                {state?.flightDetails?.return !== "One-way" && (
                  <>
                    -{" "}
                    {t("booking.actions.days", {
                      count: state?.flightDetails?.totalDays,
                    })}
                  </>
                )}
                <ArrowDown />
              </Button>
            </div>

            <SavedTravelersModal
              open={savedPickerIndex !== null}
              usedIds={Object.entries(appliedSaved)
                .filter(([slot]) => Number(slot) !== savedPickerIndex)
                .map(([, saved]) => saved.id)}
              onClose={() => setSavedPickerIndex(null)}
              onSelect={(traveler) => {
                if (savedPickerIndex === null) {
                  return;
                }
                const travelerType =
                  state?.flight?.travelerPricings?.[savedPickerIndex]
                    ?.travelerType;
                applySavedTravelerToSlot({
                  traveler,
                  index: savedPickerIndex,
                  includeContact:
                    travelerType !== "CHILD" &&
                    travelerType !== "HELD_INFANT",
                  setFieldValue,
                  setFieldError,
                  departureDate:
                    state?.flight?.itineraries?.[0]?.segments?.[0]?.departure
                      ?.at,
                  expiryMessage: t(
                    "booking.existingTravelers.expirationInvalid",
                  ),
                  expiryAcknowledgedRef,
                });
                setAppliedSaved((prev) => ({
                  ...prev,
                  [savedPickerIndex]: {
                    id: savedTravelerId(traveler),
                    name: savedTravelerName(traveler),
                  },
                }));
              }}
            />
          </>
        )}
      </Formik>

      <Dialog
        open={showExpiryModal}
        onClose={() => setShowExpiryModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(33,43,54,0.7)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,171,0,0.12)",
              flexShrink: 0,
            }}
          >
            <Iconify
              icon="eva:alert-triangle-fill"
              width={24}
              sx={{ color: "#ffab00" }}
            />
          </Box>
          {t("booking.passportWarning.title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {t("booking.passportWarning.description")}
          </Typography>

          {expiryWarnings.map((msg, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                mb: 1,
                borderRadius: "10px",
                bgcolor: "rgba(255,171,0,0.08)",
                border: "1px solid rgba(255,171,0,0.2)",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {msg}
              </Typography>
            </Box>
          ))}

          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            {t("booking.passportWarning.responsibility")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MUIButton
            onClick={() => setShowExpiryModal(false)}
            color="inherit"
            variant="outlined"
          >
            {t("booking.actions.goBackFix")}
          </MUIButton>
          <MUIButton
            variant="contained"
            color="warning"
            onClick={() => {
              state?.flight?.travelerPricings?.forEach((_, idx) => {
                expiryAcknowledgedRef.current[idx] = true;
              });
              setShowExpiryModal(false);
              pendingSubmitRef.current?.();
            }}
          >
            {t("booking.actions.understandContinue")}
          </MUIButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PassengersDetails;
